// Copyright (c) 2018 Cigent Technology Inc. All Rights Reserved.

import {Component, OnInit, HostListener} from '@angular/core';
import {HeaderService} from 'src/app/header.service';
import {UntypedFormGroup, UntypedFormBuilder, Validators} from '@angular/forms';
import {D3eService} from 'src/services/d3e-service';
import {
  ID3EPolicyResponse,
  IPolicyListItem,
  ID3EPolicy,
  IClientPolicyDefaults,
  IPolicyFeatureFlags,
  IEncryptedDirectory, IKnownGoodApp, IKnownGoodDefaults,
  IDeviceConfigEncryptedDirectory
} from 'src/types/d3ePolicy';
import {NotificationService} from 'src/services/notification-service';
import {CustomValidators} from 'src/app/shared/validators/custom-validators';
import {combineLatest, Observable, of} from 'rxjs';
import {ISubscription, ILicense, StripeProductTier} from 'src/types/subscription';
import { IManageUser, IUser } from 'src/types/user';

import {catchError, map, switchMap, shareReplay} from 'rxjs/operators';
import {ActivatedRoute} from '@angular/router';
import {ClientPolicyService} from 'src/services/client-policy.service';
import {SelectedOrgMethod} from 'src/shared/selectedOrg';
import {AppComponent} from 'src/app/app.component';
import {PageDeactivateService} from 'src/services/page-deactivate.service';
import {AdminSettingsService} from 'src/services/admin-settings.service';
import {IAdminSettings} from 'src/types/admin-settings';
import {TranslateService} from '@ngx-translate/core';
import {UntilDestroy, untilDestroyed} from '@ngneat/until-destroy';
import {IEndpointsResponse} from '../../../types/endpoints';
import {Store} from '@ngrx/store';
import {AppState} from '../../store/app.state';
import {selectUser} from '../../store/user/user.selectors';
import { DeactivatableComponent } from '../../guards/page.guard';
import { replaceGuidsWithMountLetters } from '../../../shared/guid-to-mount-letters';

function toKnownGoodApp(defaults: IKnownGoodDefaults[]) {
  const knownGoodDefaults = [];
  defaults.forEach(kg => {
    const knownGood: IKnownGoodApp = JSON.parse(JSON.stringify(kg));
    knownGoodDefaults.push(knownGood);
  });
  return knownGoodDefaults;
}


@UntilDestroy({checkProperties: true})
@Component({
    selector: 'app-global-d3e-policy',
    templateUrl: './global-d3e-policy.component.html',
    styleUrls: ['./global-d3e-policy.component.scss'],
    standalone: false
})
export class GlobalD3ePolicyComponent implements OnInit, DeactivatableComponent {
  subscriptions: ISubscription[] = [];
  devices: ILicense[] = [];
  selectedDevice: ILicense;
  selectedDeviceNonGuid: ILicense;
  user: IUser;
  searchValue = null;
  globalD3EPolicyForm: UntypedFormGroup;
  isDirty = false;
  policies: IPolicyListItem[] = [];
  loadedPolicy: number;  // ID of the policy currently loaded.
  routePolicyId: string;
  d3ePolicy: ID3EPolicy;
  d3ePolicyName: string;
  d3ePolicyEnabled: boolean;
  overrideLocalPolicy: boolean;
  allowAlwaysOn: boolean;
  clientPolicy: IClientPolicyDefaults;
  clientPolicyDefaults: IClientPolicyDefaults;

  tierStatus$: Observable<{ dd: boolean; bitLocker: boolean; agent: boolean; pba: boolean; fde: boolean; }> = combineLatest([
    this.store.select(selectUser()),
    this.headerService.orgSelectedChanged
  ]).pipe(
    switchMap(([user, selectedOrg]: [IManageUser, string]) => {
      if (selectedOrg === 'All') { selectedOrg = user?.group_id?.toString(); }
      return this.d3eService.get_subscriptions(selectedOrg);
    }),
    map((subscriptions) => ({
      dd: subscriptions.some(s => s.stripe_product.tier === StripeProductTier.PERSONAL || s.stripe_product.tier === StripeProductTier.BUSINESS),
      bitLocker: subscriptions.some(s => s.stripe_product.tier === StripeProductTier.BITLOCKER),
      agent: subscriptions.some(s => s.stripe_product.tier === StripeProductTier.AGENT),
      pba: subscriptions.some(s =>  s.stripe_product.tier === StripeProductTier.HARDWARE || s.stripe_product.tier === StripeProductTier.HARDWARE2),
      fde: subscriptions.some(s =>  s.stripe_product.tier === StripeProductTier.FDE)
    })),
    shareReplay({bufferSize: 1, refCount: true}) // Shared single subscription across the template
  );

  //  Feature toggles set from the API 
  show_force_policy_checkbox: boolean;
  show_environment_variables: boolean;

  sub_key_uuids: string[];

  policyPageConfig: IPolicyFeatureFlags = {
    knownGoodTab: false,
    deceptionTab: false,
    bitlockerTab: false,
    pbaTab: false,
    fdeTab: false,
  };

  hasProtectedExtError = false;
  hasLockedExtError = false;
  hasProtectedDirError = false;
  hasLockedDirError = false;
  hasDeceptionFileError = false;

  adminSettings: IAdminSettings = null;
  enableSubscriptionKey = false;

  // user selector
  selectedOrgId: string;
  hasUserBeenSet = false;

  fSubtitle: string;
  fEnvTip: string;
  fHideEnv: string;
  fEnvTip2: string;
  fShowEnv: string;
  fHomeEnv: string;
  fHome: string;
  fAppDataEnv: string;
  fAppData: string;
  fAllUserEnv: string;
  fAllUsers: string;
  fRootEnv: string;
  fRoot: string;
  fUserEnv: string;
  fUser: string;
  fProtExt: string;
  fProtExtPHolder: string;
  fExtInvalid: string;
  fLockedExt: string;
  fLockedExtPholder: string;
  fProtDir: string;
  fProtDirPholder: string;
  fDirInvalid: string;
  fLockedDir: string;
  fLockedDirPHolder: string;
  fDecep: string;
  fDecepPHolder: string;
  fExists: string;
  fExistsPholder: string;
  fLoad: string;
  fRemove: string;
  fRemoveDesc: string;
  fCancel: string;
  fApply: string;
  fNote: string;
  fSuccess: string;
  fUpdatingPolicy: string;
  fSelectedOrgChanged: string;
  fPolicyDidNotChange: string;

  title: string;
  subtitle: string;

  constructor(
    private headerService: HeaderService,
    private formBuilder: UntypedFormBuilder,
    private d3eService: D3eService,
    private notification: NotificationService,
    private route: ActivatedRoute,
    private policyService: ClientPolicyService,
    private appComponent: AppComponent,
    private pageDeactivateService: PageDeactivateService,
    private adminSettingsService: AdminSettingsService,
    private translate: TranslateService,
    private readonly store: Store<AppState>,
  ) {
    headerService.setContextHelp(['help.policies']);
    this.translate.get(['P_SUBTITLE', 'P_ENVTIP', 'P_HIDE_ENV', 'P_ENVTIP2', 'P_SHOW_ENV', 'P_HOME_ENVTIP',
      'P_HOME', 'P_APPDATA_ENV', 'P_APPDATA', 'P_ALLUSER_ENV', 'P_ALLUSERS', 'P_ROOT_ENV', 'P_ROOT', 'P_USER_ENV',
      'P_USER', 'P_PROTEXT', 'P_PROTEXT_PHOLDER', 'P_EXT_INVALID', 'P_LOCKEXT', 'P_LOCKEXT_PHOLDER', 'P_PROTDIR',
      'P_PROTDIR_PHOLDER', 'P_DIR_INVALID', 'P_LOCKDIR', 'P_LOCKDER_PHOLDER', 'P_DECEP', 'P_DECEP_PHOLDER', 'P_EXIST',
      'P_EXIST_PHOLDER', 'P_LOAD', 'P_REMOVE', 'P_REMOVE_DESC', 'CANCEL', 'P_APPLY', 'P_NOTE', 'TITLE_POLICIES',
      'SUBTITLE_POLICIES', 'SUCCESS', 'P_UPDATING_POLICY', 'SELECTED_ORG_CHANGED',
      'P_DID_NOT_CHANGE', 'SELECTOR_YOUR_POLICIES']).subscribe(vals => {
      this.fSubtitle = vals['P_SUBTITLE'];
      this.fEnvTip = vals['P_ENVTIP'];
      this.fHideEnv = vals['P_HIDE_ENV'];
      this.fEnvTip2 = vals['P_ENVTIP2'];
      this.fShowEnv = vals['P_SHOW_ENV'];
      this.fHomeEnv = vals['P_HOME_ENVTIP'];
      this.fHome = vals['P_HOME'];
      this.fAppDataEnv = vals['P_APPDATA_ENV'];
      this.fAppData = vals['P_APPDATA'];
      this.fAllUserEnv = vals['P_ALLUSER_ENV'];
      this.fAllUsers = vals['P_ALLUSERS'];
      this.fRootEnv = vals['P_ROOT_ENV'];
      this.fRoot = vals['P_ROOT'];
      this.fUserEnv = vals['P_USER_ENV'];
      this.fUser = vals['P_USER'];
      this.fProtExt = vals['P_PROTEXT'];
      this.fProtExtPHolder = vals['P_PROTEXT_PHOLDER'];
      this.fExtInvalid = vals['P_EXT_INVALID'];
      this.fLockedExt = vals['P_LOCKEXT'];
      this.fLockedExtPholder = vals['P_LOCKEXT_PHOLDER'];
      this.fProtDir = vals['P_PROTDIR'];
      this.fProtDirPholder = vals['P_PROTDIR_PHOLDER'];
      this.fDirInvalid = vals['P_DIR_INVALID'];
      this.fLockedDir = vals['P_LOCKDIR'];
      this.fLockedDirPHolder = vals['P_LOCKDER_PHOLDER'];
      this.fDecep = vals['P_DECEP'];
      this.fDecepPHolder = vals['P_DECEP_PHOLDER'];
      this.fExists = vals['P_EXIST'];
      this.fExistsPholder = vals['P_EXIST_PHOLDER'];
      this.fLoad = vals['P_LOAD'];
      this.fRemove = vals['P_REMOVE'];
      this.fRemoveDesc = vals['P_REMOVE_DESC'];
      this.fCancel = vals['CANCEL'];
      this.fApply = vals['P_APPLY'];
      this.fNote = vals['P_NOTE'];
      this.fSuccess = vals['SUCCESS'];
      this.fUpdatingPolicy = vals['P_UPDATING_POLICY'];
      this.fSelectedOrgChanged = vals['SELECTED_ORG_CHANGED'];
      this.fPolicyDidNotChange = vals['P_DID_NOT_CHANGE'];

      this.title = vals['TITLE_POLICIES'];
      this.subtitle = vals['SUBTITLE_POLICIES'];

      this.headerService.setOrgSelectorDefaultOption(vals['SELECTOR_YOUR_POLICIES']);
    });
    this.headerService.setTitle(this.title);
    this.headerService.setSubtitle(this.subtitle);
    this.headerService.setIcon('mdi-wallet-membership');
    this.headerService.setNavMenuText('');

    this.show_force_policy_checkbox = false;
    this.show_environment_variables = false;
    this.d3ePolicyEnabled = true;
    this.overrideLocalPolicy = false;
    this.allowAlwaysOn = false;
    this.route = route;
    this.clientPolicy = {} as IClientPolicyDefaults;

    this.headerService.showUserFilter(true);

    this.headerService.orgSelectedChanged
      .pipe(untilDestroyed(this))
      .subscribe(value => {
        this.routePolicyId = null;
        this.selectedOrgId = value; // Note, if discarded, this will get called again
        pageDeactivateService.canDeactivateUserSelector(this, this.appComponent);
      });

    // NOTE: if any of the validator patterns are changed, changes must also be made in policy_service.py
    this.globalD3EPolicyForm = this.formBuilder.group({
      name: ['', Validators.required],
      protectedExts: ['', CustomValidators.patternValidator(/^[a-zA-Z0-9,]+$/, {hasInvalidChars: true})],
      lockedExts: ['', CustomValidators.patternValidator(/^[a-zA-Z0-9,]+$/, {hasInvalidChars: true})],
      protectedDirs: ['', CustomValidators.patternValidator(/^[^\x00-\x1f\x7f\*\?"<>\|\"\'\;/]+$/, {hasInvalidChars: true})],
      ransomExts: ['', CustomValidators.patternValidator(/^[a-zA-Z0-9,]+$/, {hasInvalidChars: true})],
      // tslint:disable-next-line:max-line-length
      lockedDirs: ['', CustomValidators.patternValidator(/^[^\x00-\x1f\x7f\*\?"<>\|\"\'\;/]+$/, {hasInvalidChars: true})],
      deceptionFiles: ['', CustomValidators.patternValidator(/^[^\x00-\x1f\x7f\^\*\?"<>\|\"\'\;/]+$/, {hasInvalidChars: true})],
      forcePolicyCheckbox: false,
    });

    this.route.queryParams.subscribe(params => {
      const selectedOrgId = params['selectedOrgId'];
      if (selectedOrgId !== undefined && selectedOrgId != null) {
        const current_fb_uid = localStorage.getItem('fb_uid');
        this.selectedOrgId = selectedOrgId;
        localStorage.setItem('fb_uid', this.selectedOrgId);
        this.appComponent.orgSelectChanged(selectedOrgId);
        if (current_fb_uid !== selectedOrgId) {
          this.notification.show(this.fSelectedOrgChanged);
        }
      }
    });

  }

  // Do NOT Delete function, this function is being used!
  // Implementation of interface DeactivatableComponent in page.guard.ts
  userValueChanged() {
    if (this.hasUserBeenSet) {
      this.getPolicyDefault();
      this.getD3EPolicies();
      this.getDevices();
    }
    this.hasUserBeenSet = true;
    this.isDirty = false;
  }

  // Implementation of interface DeactivatableComponent in page.guard.ts
  getIsDirty(): boolean {
    return this.isDirty;
  }

  // Implementation of interface DeactivatableComponent in page.guard.ts
  canDeactivateResult(result: boolean): void {
    // no-op
  }

  get g() {
    return this.globalD3EPolicyForm.controls;
  }

  ngOnInit() {
    this.route.paramMap.subscribe(params => {
      this.routePolicyId = params.get('policy-id');
      this.getAdminSettings();
      this.getPolicyPageConfig();
      this.getPolicyDefault();
      this.getD3EPolicies();
      this.getDevices();
    });
  }

  getPolicyDefault() {
    const selectedOrg = SelectedOrgMethod.getSelectedOrg(this.selectedOrgId);
    this.policyService.getClientPolicyDefaults(selectedOrg)
      .pipe(untilDestroyed(this))
      .subscribe(clientPolicyDefaults => {
          this.clientPolicy = JSON.parse(JSON.stringify(clientPolicyDefaults));
          this.clientPolicyDefaults = JSON.parse(JSON.stringify(clientPolicyDefaults));
        }
      );
  }

  getPolicyPageConfig() {
    this.d3eService.getPolicyPageConfig()
      .pipe(untilDestroyed(this))
      .subscribe(result => {
        this.policyPageConfig = result;
      });
  }

  loadPolicy(policyId: number) {
    this.getD3EPolicyById(policyId);
    this.isDirty = false;
  }

  deletePolicy(policyId: number) {
    const selectedOrg = SelectedOrgMethod.getSelectedOrg(this.selectedOrgId);
    this.d3eService.deleteD3EPolicy(policyId, selectedOrg)
      .subscribe({
        next: () => {
          this.getD3EPolicies();
        }
      });
  }

addPolicy(policyName: string): void {
  const selectedOrg = SelectedOrgMethod.getSelectedOrg(this.selectedOrgId);

  this.d3eService.postD3EPolicy(policyName, {}, selectedOrg)
    .subscribe({
      next: (result: { new_id: number }) => {
        this.loadedPolicy = result.new_id;
        this.getD3EPolicies(false);
        this.getD3EPolicyById(this.loadedPolicy);
      },
      error: (err: any) => {
        let message: string;
        if (err.error === 'DuplicateName') {
          message = this.translate.instant('ERROR_DUPLICATE_NAME');
        } else {
          message = this.translate.instant('ERROR_OCCURRED_TITLE');
        }
        this.notification.showError(message);
      }
    });
}

  clonePolicy(newPolicyName: string) {
    const selectedOrg = SelectedOrgMethod.getSelectedOrg(this.selectedOrgId);
    this.d3eService.cloneD3EPolicy(this.loadedPolicy, newPolicyName, selectedOrg).subscribe({
      next: () => this.getD3EPolicies(false),
      error: error => {
        let message: string;
        if (error.error === 'DuplicateName') {
          message = this.translate.instant('ERROR_DUPLICATE_NAME');
        } else {
          message = this.translate.instant('ERROR_OCCURRED_TITLE');
        }
        this.notification.showError(message);
      }
    });
  }

  renamePolicy(event) {
    const selectedOrg = SelectedOrgMethod.getSelectedOrg(this.selectedOrgId);
    const {id, newName} = event;
    this.d3eService.renameD3EPolicy(id, newName, selectedOrg)
      .pipe(untilDestroyed(this))
      .subscribe({
        next: () => {
          this.getD3EPolicies(false);
        },
        error: () => {
          this.notification.showError(this.translate.instant('ERROR_RENAMING_POLICY'));
        },
      });
  }

  changePriorities(policyIds) {
    const selectedOrg = SelectedOrgMethod.getSelectedOrg(this.selectedOrgId);

    this.d3eService.setD3EPolicyPriorities(policyIds, selectedOrg)
      .subscribe({
          next: () => {
              this.getD3EPolicies(false);
          },
          error: () => {
            this.getD3EPolicies(false);
          }
      });
  }

  getD3EPolicies(initialize = true) {
    const selectedOrg = SelectedOrgMethod.getSelectedOrg(this.selectedOrgId);

    this.d3eService
      .getD3EPolicies(selectedOrg)
      .pipe(untilDestroyed(this))
      .subscribe({
        next: (policies: IPolicyListItem[]) => {
          this.policies = policies;
          if (initialize && policies.length > 0) {
            if (this.routePolicyId) {
              this.getD3EPolicyById(+this.routePolicyId);
            } else {
              this.getD3EPolicyById(policies[0].id);
            }
          }
          if (initialize && policies.length === 0) {
            // The GUI should not be responsible for creating the policy.
            // The getD3EPolicies call will now handle this if it does not exist.
            this.getD3EPolicies(false);
          }
        },
        error: (err) => {
          this.notification.showError(this.translate.instant('ERROR_GETTING_POLICIES'));
        }
      });
  }

  getD3EPolicyById(id) {
    this.loadedPolicy = id;
    this.getD3EPolicy();
  }

  getD3EPolicy() {
    const selectedOrg = SelectedOrgMethod.getSelectedOrg(this.selectedOrgId);
this.d3eService
      .getGlobalD3EPolicy(this.loadedPolicy, selectedOrg)
      .pipe(untilDestroyed(this))
      .subscribe({
        next: (policy_response: ID3EPolicyResponse) => {
          const { overrideLocalPolicy, enabled, allowAlwaysOn } = policy_response;
          this.show_force_policy_checkbox = policy_response.show_force_policy_checkbox || false;
          this.show_environment_variables = policy_response.show_environment_variables || false;
          this.d3ePolicy = policy_response.policy;
          this.overrideLocalPolicy = this.show_force_policy_checkbox ? !!overrideLocalPolicy : false;
          this.allowAlwaysOn = allowAlwaysOn;
          this.d3ePolicyEnabled = enabled == null ? true : enabled; // == checks for null and undefined
          this.sub_key_uuids = policy_response.sub_key_uuids || [];
          this.setGlobalD3EPolicyForm(policy_response);
        },
        error: () => {
          this.notification.showError(this.translate.instant('ERROR_GETTING_POLICY'));
        }
      });

  }

  getAdminSettings() {
    const selectedOrg = SelectedOrgMethod.getSelectedOrg(this.selectedOrgId);

    this.adminSettingsService
      .getAdminSettings(selectedOrg)
      .pipe(untilDestroyed(this))
      .subscribe(
        settings => {
          this.adminSettings = settings;
          if (settings) {
            this.enableSubscriptionKey = settings.enable_subscription_key;
          }
        }
      );
  }

  onOverrideLocalPolicyChanged(isEnabled: boolean) {
    this.overrideLocalPolicy = isEnabled;
    this.isDirty = true;
  }

  allowAlwaysOnChanged(isEnabled: boolean) {
    this.allowAlwaysOn = isEnabled;
    this.isDirty = true;
  }

  setGlobalD3EPolicyForm(policy_response: ID3EPolicyResponse) {
    this.show_force_policy_checkbox = policy_response.show_force_policy_checkbox || false;
    this.show_environment_variables = policy_response.show_environment_variables || false;
    if (!(Object.entries(policy_response.policy).length === 0 && policy_response.policy.constructor === Object)) {
      const {protectedExts, lockedExts, protectedDirs, lockedDirs, ransomExts, deceptionFiles} = policy_response.policy;
      const {overrideLocalPolicy} = policy_response;
      this.d3ePolicyName = policy_response.name;
      // this.overrideLocalPolicy = overrideLocalPolicy;
      this.globalD3EPolicyForm.setValue({
        name: policy_response.name,
        protectedExts: protectedExts && protectedExts.filter(x => x.length).join(',') || [],
        lockedExts: lockedExts && lockedExts.filter(x => x.length).join(',') || [],
        protectedDirs: protectedDirs && protectedDirs.filter(x => x.length).join(',') || [],
        lockedDirs: lockedDirs && lockedDirs.filter(x => x.length).join(',') || [],
        ransomExts: ransomExts && ransomExts.filter(x => x.length).join(',') || [],
        deceptionFiles: deceptionFiles && deceptionFiles.filter(x => x.length).join(',') || [],
        forcePolicyCheckbox: this.show_force_policy_checkbox ? !!overrideLocalPolicy : false,
      });
    } else {
      const {overrideLocalPolicy} = policy_response;
      this.d3ePolicyName = '';
      this.globalD3EPolicyForm.setValue({
        name: '',
        protectedExts: [],
        lockedExts: [],
        protectedDirs: [],
        lockedDirs: [],
        ransomExts: [],
        deceptionFiles: [],
        forcePolicyCheckbox: this.show_force_policy_checkbox ? !!overrideLocalPolicy : false,
      });
    }
  }

  onCancel() {
    this.isDirty = false;
    this.hasProtectedExtError = false;
    this.hasLockedExtError = false;
    this.hasLockedDirError = false;
    this.hasProtectedDirError = false;
    this.hasDeceptionFileError = false;
    this.loadPolicy(this.loadedPolicy);
  }

  onEditorCanceled() {
    this.isDirty = false;
    this.loadPolicy(this.loadedPolicy);
  }

  getDevices() {
    const selectedOrg = SelectedOrgMethod.getSelectedOrg(this.selectedOrgId);

    const query = {
      'search': this.searchValue,
      'where': {'deactivated': 0},
      'selectedOrgId': selectedOrg,
    };
    this.d3eService
      .get_home_devices(query)
      .pipe(
        catchError(() => of([])),
        untilDestroyed(this)
      )
      .subscribe({
        next: (homeDevicesResponse: IEndpointsResponse) => {
          this.devices = homeDevicesResponse.devices;
          if (homeDevicesResponse.devices.length < homeDevicesResponse.total) {
            const lastRow = {
              id: -100,
              computer_name: '... ' + (homeDevicesResponse.total - homeDevicesResponse.devices.length) + ' more ...',
            };
            this.devices.push(<ILicense>lastRow);
          }
        },
        error: (err) => {
          // TODO: Do something better with the error, like notify the user.
          console.error(`Error retrieving devices: ${err}`);
        }
      });
  }

  getSelectedDevice() {
    this.d3eService.get_home_device(this.selectedDevice.uuid)
      .pipe(
        map((license: ILicense) => replaceGuidsWithMountLetters(license)),
        untilDestroyed(this)
      )
      .subscribe({
        next: (d) => {
          this.selectedDeviceNonGuid = d;
          this.loadSelectedDevice();
        },
        error: (err) => {
          // TODO: Do something better with the error, like notify the user.
          console.error(`Error retrieving device: ${err}`);
        }
      });
  }

  onLoadDeviceSettings(device: ILicense) {
    this.selectedDevice = device;
    this.getSelectedDevice();
  }

  onPolicyIsDirty(isDirty) {
    this.isDirty = isDirty;
  }

  getEncryptedDirectories(encryptedDirs: IDeviceConfigEncryptedDirectory[]) {
    const encryptedDirectories: IEncryptedDirectory[] = [];
    encryptedDirs.forEach(dir => {
      let hasSub = false;
      dir.key_data.forEach(key => {
        if (key.scope === 'subscription') {
          hasSub = true;
        }
      });
      if (hasSub) {
        encryptedDirectories.push(dir);
      }
    });

    return encryptedDirectories;
  }

  loadSelectedDevice() {
    if (!this.selectedDeviceNonGuid.config) {
      this.notification.show(this.fPolicyDidNotChange);
      return;
    }

    const settings = this.clientPolicyDefaults.sections.find(s => s.name.toLocaleLowerCase() === 'settings').groups[0].settings;
    const defaultSyncInterval = settings.find(s => s.name.toLowerCase() === 'syncinterval').value;
    const defaultSyncIntervalMetered = settings.find(s => s.name.toLowerCase() === 'syncintervalmetered').value;
    const defaultLicenseInterval = settings.find(s => s.name.toLowerCase() === 'licenseinterval').value;
    const defaultLicenseIntervalMetered = settings.find(s => s.name.toLowerCase() === 'licenseintervalmetered').value;
    const defaultForcePreAutValues = settings.find(s => s.name.toLowerCase() === 'forcepreauthvalues').value;
    const defaultPreAuthFileAccessCount = settings.find(s => s.name.toLowerCase() === 'preauthfileaccesscount').value;
    const defaultPreAuthFileAccessTimeout = settings.find(s => s.name.toLowerCase() === 'preauthfileaccesstimeout').value;
    const newKnownGood = this.getKnownGood();

    this.d3ePolicy = {
      'disabledExts': this.selectedDeviceNonGuid.config.disabledExts as string[] || [],
      'protectedExts': this.selectedDeviceNonGuid.config.protectedExts as string[] || [],
      'encryptedProtectedDirs': this.getEncryptedDirectories(this.selectedDeviceNonGuid.config.encryptedProtectedDirs),
      'lockedExts': this.selectedDeviceNonGuid.config.lockedExts as string[] || [],
      'encryptedLockedDirs': this.getEncryptedDirectories(this.selectedDeviceNonGuid.config.encryptedLockedDirs),
      'disabledDirs': this.selectedDeviceNonGuid.config.disabledDirs as string[] || [],
      'protectedDirs': this.selectedDeviceNonGuid.config.protectedDirs as string[] || [],
      'lockedDirs': this.selectedDeviceNonGuid.config.lockedDirs as string[] || [],
      'ransomExts': this.selectedDeviceNonGuid.config.ransomExts as string[] || null,
      'deceptionFiles': this.selectedDeviceNonGuid.config.deceptionFiles as string[] || [],
      'protectedVolumes': this.selectedDeviceNonGuid.config.protectedVolumes as string[] || [],
      'syncInterval': this.selectedDeviceNonGuid.config.syncInterval || defaultSyncInterval,
      'syncIntervalMetered': this.selectedDeviceNonGuid.config.syncIntervalMetered || defaultSyncIntervalMetered,
      'licenseInterval': this.selectedDeviceNonGuid.config.licenseInterval || defaultLicenseInterval,
      'licenseIntervalMetered': this.selectedDeviceNonGuid.config.licenseIntervalMetered || defaultLicenseIntervalMetered,
      'forcePreAuthValues': this.selectedDeviceNonGuid.config.forcePreAuthValues || defaultForcePreAutValues,
      'preAuthFileAccessCount': this.selectedDeviceNonGuid.config.preAuthFileAccessCount || defaultPreAuthFileAccessCount,
      'preAuthFileAccessTimeout': this.selectedDeviceNonGuid.config.preAuthFileAccessTimeout ?? defaultPreAuthFileAccessTimeout,
      'knownGood': newKnownGood
    } as ID3EPolicy;

    const protectedExts = this.selectedDeviceNonGuid.config.protectedExts as string[];
    const lockedExts = this.selectedDeviceNonGuid.config.lockedExts as string[];
    const protectedDirs = this.selectedDeviceNonGuid.config.protectedDirs as string[];
    const lockedDirs = this.selectedDeviceNonGuid.config.lockedDirs as string[];
    const ransomExts = this.selectedDeviceNonGuid.config.ransomExts as string[];
    const deceptionFiles = this.selectedDeviceNonGuid.config.deceptionFiles as string[];
    this.d3ePolicyName = this.globalD3EPolicyForm.controls.name.value;
    this.overrideLocalPolicy = this.selectedDeviceNonGuid.config.overrideLocalPolicy || false;
    this.allowAlwaysOn = this.selectedDeviceNonGuid.config.allowAlwaysOn || false;
    this.globalD3EPolicyForm.setValue({
      name: this.globalD3EPolicyForm.controls.name.value,
      protectedExts: protectedExts && protectedExts.filter(x => x.length).join(',') || [],
      lockedExts: lockedExts && lockedExts.filter(x => x.length).join(',') || [],
      protectedDirs: protectedDirs && protectedDirs.filter(x => x.length).join(',') || [],
      lockedDirs: lockedDirs && lockedDirs.filter(x => x.length).join(',') || [],
      ransomExts: ransomExts && ransomExts.filter(x => x.length).join(',') || [],
      deceptionFiles: deceptionFiles && deceptionFiles.filter(x => x.length).join(',') || [],
      forcePolicyCheckbox: this.globalD3EPolicyForm.controls['forcePolicyCheckbox'].value,
    });

    this.isDirty = true;
  }

  private getKnownGood() {
    const incomingKnownGood: IKnownGoodApp[] = this.selectedDeviceNonGuid.config.knownGood;

    const knownGoodDefaults = this.clientPolicyDefaults.sections.filter(s => {
        return ['known good'].includes(s.name.toLowerCase());
      }
    );

    let newKnownGood: IKnownGoodApp[] = [];
    if (knownGoodDefaults && knownGoodDefaults.length === 1) {
      const policySection = knownGoodDefaults[0];
      if (policySection.name.toLowerCase() === 'known good') {
        const knownGoodDefaultValues = policySection.groups[0];
        newKnownGood = toKnownGoodApp(knownGoodDefaultValues['knownGood']['defaults']);
      } else {
        console.error('got incorrect policy section ' + policySection);
      }
    } else {
      console.error('could not get known good defaults');
    }

    for (let i = 0; i < incomingKnownGood.length; i++) {
      const importedKnownGood = incomingKnownGood[i];
      let found = false;
      for (let j = 0; j < newKnownGood.length; j++) {
        if (newKnownGood[j].app === importedKnownGood.app) {
          newKnownGood[j] = importedKnownGood;
          found = true;
          break;
        }
      }
      if (!found) {
        newKnownGood.push(importedKnownGood);
      }
    }
    return newKnownGood;
  }

  onEditorSaved() {
    this.isDirty = false;
    this.getD3EPolicy();
  }

  @HostListener('window:beforeunload', ['$event'])
  unloadComponent() {
    return !this.isDirty;
  }

}
